<script setup lang="ts">
import { onBeforeMount, ref, toRefs, computed, watch } from "vue";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import { useExplorerStore } from "@/modules/explorer/store";
import DeleteAlertDialog from "@/modules/alerts/components/DeleteAlertDialog.vue";
import ConfigureTiming from "@/ui/components/ConfigureTiming.vue";
import DocumentAlertDO = App.Data.Alerts.DocumentAlertDO;
import NotificationTypeDO = App.Data.NotificationTypeDO;
import LocaleNumberInput from "@/ui/components/LocaleNumberInput.vue";
import { useAlertsRedirect } from "@/modules/explorer/composables/useAlertsRedirect";

const { redirectBackToAlertsPage } = useAlertsRedirect();

const isDialogVisible = defineModel<boolean>();

const props = defineProps<{
	columnProperties: App.Data.Alerts.TableAlertPropertiesDTO;
}>();

const { columnProperties } = toRefs(props);

const explorerStore = useExplorerStore();

const isAlreadySavedAlert = computed(() => {
	return (
		explorerStore.explorerState.lastOpenedSavedDocument?.alert?.id &&
		shallowEqual(
			{ ...columnProperties.value },
			{
				...explorerStore.explorerState.lastOpenedSavedDocument.alert.properties,
			}
		)
	);
});

const documentAlreadyHasAlert = computed(
	() => explorerStore.explorerState.lastOpenedSavedDocument?.alert?.id
);

const alertVariants = computed(() => [
	"first_time",
	"every_time",
	"first_time_per_row",
]);

function shallowEqual(obj1, obj2) {
	if (obj1 === obj2) {
		return true;
	}

	const keys1 = Object.keys(obj1);
	const keys2 = Object.keys(obj2);

	if (keys1.length !== keys2.length) {
		return false;
	}

	for (const key of keys1) {
		if (obj1[key] !== obj2[key]) {
			return false;
		}
	}

	return true;
}

const notificationTypes = ref<NotificationTypeDO[]>([]);

const documentAlert = ref<DocumentAlertDO>({
	id: explorerStore.explorerState.lastOpenedSavedDocument?.alert?.id,
	stored_document_id: explorerStore.explorerState.lastOpenedSavedDocument?.id,
	type: "above_threshold",
	properties: columnProperties,
	variant: "every_time",
	value: 0,
	check_interval_type: "every_minute",
	check_interval_specification: null,
	notification_type_id: 1,
	is_active: true,
});

watch(
	() => explorerStore.explorerState.lastOpenedSavedDocument?.alert?.id,
	(val) => (documentAlert.value.id = val)
);

const saveAlert = async () => {
	if (isAlreadySavedAlert.value) {
		await updateExistingAlert();
	} else {
		await saveNewAlert();
	}
};

const saveNewAlert = async () => {
	documentAlert.value.stored_document_id =
		explorerStore.explorerState.lastOpenedSavedDocument.id;
	const response = await toastApiResult(
		useApiClient("alerts").create(documentAlert.value)
	);

	if (response.isOk()) {
		explorerStore.explorerState.lastOpenedSavedDocument.alert = response.value;
		isDialogVisible.value = false;
		await redirectBackToAlertsPage();
	}
};

const usedMetricFormat = computed(() => {
	if (documentAlert.value?.properties?.value_column === "deltaRelative") {
		return "percentage";
	}

	return (
		explorerStore.document.data_tables
			.flatMap((dataTable) => dataTable.metrics)
			.find(
				(metric) =>
					metric.name_clean === documentAlert.value?.properties?.metric_name
			)?.format ?? "default"
	);
});

const updateExistingAlert = async () => {
	const response = await toastApiResult(
		useApiClient("alerts").update(documentAlert.value.id, documentAlert.value)
	);

	if (response.isOk()) {
		explorerStore.explorerState.lastOpenedSavedDocument.alert = response.value;
		isDialogVisible.value = false;
	}
};

onBeforeMount(async () => {
	if (isAlreadySavedAlert.value) {
		documentAlert.value = {
			...explorerStore.explorerState.lastOpenedSavedDocument.alert,
		};
	}

	const response = await useApiClient(
		"notifications"
	).getAvailableNotificationTypes();
	if (response.isOk()) {
		notificationTypes.value = response.value;

		notificationTypes.value = notificationTypes.value.filter(
			(notificationType) => notificationType.name_clean !== "export"
		);
	}
});

const isDeleteDialogVisible = ref(false);

const deleteAlert = () => {
	isDeleteDialogVisible.value = true;
};

const onAlertDeleted = () => {
	explorerStore.explorerState.lastOpenedSavedDocument.alert = null;
	isDialogVisible.value = false;
};

const isReady = computed(() => {
	return (
		explorerStore.explorerState.lastOpenedSavedDocument !== null &&
		explorerStore.explorerState.lastOpenedSavedDocument.active_document_uuid ===
			explorerStore.explorerState.lastOpenedSavedDocumentDocumentUuid
	);
});
</script>

<template>
	<delete-alert-dialog
		v-model="isDeleteDialogVisible"
		:alert-id="documentAlert.id ?? null"
		:alert-title="
			explorerStore.explorerState.lastOpenedSavedDocument?.title ?? ''
		"
		@deleted="onAlertDeleted"
	/>
	<v-dialog v-model="isDialogVisible" persistent width="60vw" max-width="600px">
		<v-card elevation="0" class="help">
			<v-toolbar>
				<v-toolbar-title
					><i class="fa-duotone fa-bell-on fa-xl mr-3"></i
					>{{ isAlreadySavedAlert ? "Modify" : "Set up" }}
					Alert

					<v-progress-circular
						class="ml-2"
						v-if="!isReady"
						indeterminate
						size="20"
						width="2"
					/>
				</v-toolbar-title>
				<v-spacer></v-spacer>

				<v-btn icon @click="isDialogVisible = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>
			<v-divider />
			<div class="pa-5">
				<div>
					<label class="text-h6">Alert me when:</label>
					<v-radio-group hide-details v-model="documentAlert.type" inline>
						<v-radio value="below_threshold" label="Any value goes below" />
						<v-radio value="above_threshold" label="Any value goes above" />
					</v-radio-group>
				</div>
				<div class="pb-5">
					<label class="pr-3 text-h6" style="line-height: 40px"
						>Threshold value:</label
					>
					<div>
						<locale-number-input
							v-model="documentAlert.value"
							:format="usedMetricFormat"
							hide-details
							data-testid="Threshold value"
						/>
					</div>
				</div>
				<div>
					<label class="text-h6">Alert when value crosses threshold:</label>
					<v-radio-group
						v-model="documentAlert.variant"
						class="d-flex"
						color="primary"
						hide-details
					>
						<v-radio
							v-for="(option, i) in alertVariants"
							:key="i"
							:value="option"
							color="primary"
						>
							<template v-slot:label>
								<span style="text-transform: capitalize">{{
									option.replaceAll("_", " ")
								}}</span>
							</template>
						</v-radio>
					</v-radio-group>
				</div>
				<v-divider class="my-1 mb-4" />
				<configure-timing
					v-model:interval-type="documentAlert.check_interval_type"
					v-model:interval-specification="
						documentAlert.check_interval_specification
					"
				/>
				<div>
					<div>
						<label class="pr-3 text-h6" style="line-height: 40px"
							>Severity of alert:</label
						>
					</div>
					<div>
						<v-select
							:items="notificationTypes"
							v-model="documentAlert.notification_type_id"
							item-title="name_clean"
							item-value="id"
							variant="filled"
							density="compact"
							hide-details
						></v-select>
					</div>
				</div>

				<v-alert
					v-if="documentAlreadyHasAlert && !isAlreadySavedAlert"
					type="warning"
					class="mt-4"
				>
					There is already an alert saved for this document. If you save this,
					it will overwrite the previous alert.
				</v-alert>
			</div>

			<v-divider />
			<v-card-actions>
				<span v-if="!isReady" class="text-caption">
					Waiting for document to be saved
				</span>
				<v-btn
					prepend-icon="mdi-trash-can-outline"
					color="error"
					variant="tonal"
					@click="deleteAlert"
					v-if="isAlreadySavedAlert"
				>
					Delete this alert
				</v-btn>
				<v-spacer />
				<v-btn color="error" variant="text" @click="isDialogVisible = false"
					>Cancel
				</v-btn>
				<v-btn
					color="primary"
					variant="tonal"
					:disabled="!isReady"
					@click="saveAlert"
					>Save
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss"></style>
